import React from 'react';

import CommentIcon from '@mui/icons-material/Comment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Badge, Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import moment from 'moment-timezone';

import { SKIPCAR_CG_PUBLIC_AUTHOR } from '../../constants/comments';

interface Comment {
  user?: string;
  user_name?: string;
  date?: string;
  created_at?: string;
  comment: string;
  user_role?: string;
}

interface CommentsSummaryProps {
  comments: Comment[];
  commentType: 'internal' | 'external';
  onViewComments: () => void;
  onAddComment?: () => void;
  maxWidth?: number;
  showAddButton?: boolean;
  registrationId?: number;
  /**
   * Rôle du compte qui regarde. Détermine si les auteurs Skipcar sont fondus
   * dans un intitulé unique (vue agence) ou nommés (vue interne). Absent =
   * traité comme une vue agence, le choix prudent.
   */
  viewerRole?: string;
}

const CommentsSummary: React.FC<CommentsSummaryProps> = ({
  comments,
  commentType,
  onViewComments,
  onAddComment,
  maxWidth = 200,
  showAddButton = true,
  registrationId,
  viewerRole,
}) => {
  const formatCommentDate = (dateString: string) => {
    return moment(dateString).tz('Europe/Paris').format('DD/MM/YYYY HH:mm');
  };

  // Defensive : la colonne JSON `Comments` peut historiquement contenir un
  // objet (ex: { rejection_reason }) plutôt qu'un array. Sans ce guard,
  // `comments.slice(-2)` crashe avec "comments.slice is not a function" et
  // fait sauter toute la page /list-cg pour les comptes god/arbitrator.
  const commentsArray: Comment[] = Array.isArray(comments) ? comments : [];

  const getLastTwoComments = () => {
    if (commentsArray.length === 0) return [];
    return commentsArray.slice(-2).reverse(); // Prend les 2 derniers et les inverse pour afficher le plus récent en premier
  };

  const lastTwoComments = getLastTwoComments();
  const commentCount = commentsArray.length;

  const getCommentTypeConfig = () => {
    if (commentType === 'internal') {
      return {
        label: 'Commentaires internes',
        color: '#1976d2' as const,
        icon: <CommentIcon sx={{ fontSize: 16 }} />,
        chipColor: 'primary' as const,
      };
    } else {
      return {
        label: 'Commentaires externes',
        color: '#ed6c02' as const,
        icon: <CommentIcon sx={{ fontSize: 16 }} />,
        chipColor: 'warning' as const,
      };
    }
  };

  const config = getCommentTypeConfig();

  if (commentCount === 0) {
    return (
      <Box sx={{ minWidth: maxWidth, textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            mb: 1,
            fontSize: '0.875rem',
          }}
        >
          Aucun commentaire {commentType === 'internal' ? 'interne' : 'externe'}
        </Typography>
        {showAddButton && onAddComment && (
          <Button
            size="small"
            variant="outlined"
            onClick={onAddComment}
            startIcon={config.icon}
            className="secondaryDealActionButton"
            sx={{
              fontSize: '0.75rem',
              py: 0.5,
              px: 1,
            }}
          >
            Ajouter
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: maxWidth }}>
      {/* Badge avec le nombre de commentaires */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
        <Badge
          badgeContent={commentCount}
          color={config.chipColor}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              minWidth: 18,
              height: 18,
            },
          }}
        >
          {config.icon}
        </Badge>
        <Chip
          label={config.label}
          size="small"
          color={config.chipColor}
          variant="outlined"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            transform: 'translateY(-5px)', // Remonte le chip de 5px
          }}
        />
      </Box>

      {/* Deux derniers commentaires */}
      {lastTwoComments.map((comment, index) => (
        <Box
          key={index}
          sx={{
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderRadius: 1,
            p: 1.5,
            mb: index < lastTwoComments.length - 1 ? 1 : 1,
            border: `1px solid ${config.color}20`,
            position: 'relative',
            opacity: index === 0 ? 1 : 0.8, // Le plus récent est plus visible
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              backgroundColor: config.color,
              borderRadius: '0 2px 2px 0',
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            {commentType === 'internal' || comment.user_role === 'agency' || viewerRole !== 'agency'
              ? comment.user || comment.user_name || 'Utilisateur'
              : SKIPCAR_CG_PUBLIC_AUTHOR}{' '}
            - {formatCommentDate(comment.date || comment.created_at || '')}
          </Typography>
          <Tooltip title={comment.comment} placement="top" arrow>
            <Typography
              variant="body2"
              sx={{
                fontSize: '1rem',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                cursor: 'pointer',
              }}
              onClick={onViewComments}
            >
              {comment.comment}
            </Typography>
          </Tooltip>
        </Box>
      ))}

      {/* Boutons d'action */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} data-registration-id={registrationId}>
        <Button
          size="small"
          variant="contained"
          onClick={onViewComments}
          startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
          className="dealActionButton"
          sx={{
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.5,
          }}
        >
          Voir tous ({commentCount})
        </Button>

        {showAddButton && onAddComment && (
          <Button
            size="small"
            variant="outlined"
            onClick={onAddComment}
            startIcon={config.icon}
            className={`secondaryDealActionButton ${commentType === 'external' ? 'external-comments-button' : ''}`}
            sx={{
              fontSize: '0.75rem',
              py: 0.5,
              px: 1,
            }}
          >
            Ajouter
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(CommentsSummary);
